// DS — pole tekstowe / e-mail / liczba (artboard 02).
// Etykieta nad polem, zdanie pomocnicze pod polem; błąd mówi, co poprawić,
// i trafia do aria (aria-invalid + aria-describedby), nie tylko do koloru.
//
// Sesja 12 (MAPA 12, propozycja 4): wariant daty i godziny — `type="datetime-local"`
// dostaje wartość mono i ikonę kalendarza z zestawu (lucide 18) w prawym rogu pola;
// stany jak pole tekstowe (domyślny / błąd ze zdaniem). Wariant wybiera TYP pola,
// nie osobny komponent: okno Autopilota to ten sam `Field`, co reszta produktu.

import { useId } from 'react'
import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react'
import Icon from '../media/Icon'

type Base = {
  /** Etykieta nad polem. */
  label?: string
  /** Zdanie pomocnicze pod polem. */
  hint?: string
  /** Zdanie błędu pod polem; ustawia aria-invalid. */
  error?: string
  className?: string
}

export type FieldProps = Base & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & { multiline?: false }
export type TextareaFieldProps = Base & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & { multiline: true }

export function Field(props: FieldProps | TextareaFieldProps) {
  const { label, hint, error, className = '', id, multiline, ...rest } = props as (FieldProps | TextareaFieldProps) & { multiline?: boolean }
  const auto = useId()
  const inputId = id ?? `fld-${auto}`
  // Opis OD WOŁAJĄCEGO dokleja się do własnych opisów pola, zamiast przepaść pod nimi:
  // ekran, który niesie fakt wyłącznie dla czytnika (tekst `sr-only`), nie ma innej
  // drogi go podpiąć, a `{...rest}` stało przed `aria` i kasowało atrybut.
  const describedBy = [
    error ? `${inputId}-err` : null,
    hint ? `${inputId}-hint` : null,
    rest['aria-describedby'] ?? null,
  ].filter(Boolean).join(' ') || undefined

  const aria = {
    id: inputId,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
  }

  const datetime = !multiline && (rest as InputHTMLAttributes<HTMLInputElement>).type === 'datetime-local'

  return (
    <div className={['field', error && 'is-error', datetime && 'field-dt', className].filter(Boolean).join(' ')}>
      {label ? <label htmlFor={inputId}>{label}</label> : null}
      {multiline
        ? <textarea {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)} {...aria} />
        : datetime
          ? (
            <span className="field-dt-wrap">
              <input {...(rest as InputHTMLAttributes<HTMLInputElement>)} {...aria} />
              <span className="field-dt-ic" aria-hidden="true"><Icon name="Calendar" size={18} /></span>
            </span>
          )
          : <input {...(rest as InputHTMLAttributes<HTMLInputElement>)} {...aria} />}
      {error ? <div className="field-error" id={`${inputId}-err`} role="alert">{error}</div> : null}
      {hint ? <div className="field-hint" id={`${inputId}-hint`}>{hint}</div> : null}
    </div>
  )
}

export default Field
